import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useSystemStats, useJobsByWorker, useThroughput, useHealthStats } from "@/hooks/api";
import type { NodeStats, HostStats, DiskStats, SwapStats } from "@/hooks/api/useSystem";

function StatCard({ label, value, variant }: { label: string; value: number; variant?: string }) {
  const color =
    variant === "pending" ? "text-admin-caution-400" :
    variant === "running" ? "text-admin-info-400" :
    variant === "completed" ? "text-admin-success-400" :
    variant === "failed" ? "text-admin-danger-400" :
    "text-admin-text-primary";

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <p className="text-sm text-admin-text-secondary">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, unit, warn }: { label: string; value: string | number; unit?: string; warn?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-xs text-admin-text-secondary">{label}</span>
      <span className={`text-sm font-mono tabular-nums ${warn ? "text-admin-danger-400" : "text-admin-text-primary"}`}>
        {value}{unit && <span className="text-xs text-admin-text-subtle ml-0.5">{unit}</span>}
      </span>
    </div>
  );
}

function formatUptime(seconds: number) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTimeAgo(isoString: string) {
  const seconds = Math.round((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatGb(mb: number) {
  return (mb / 1024).toFixed(1);
}

function DiskStat({ disk }: { disk: DiskStats }) {
  return (
    <MiniStat
      label={`Disk ${disk.mount}`}
      value={`${disk.usagePercent}% (${formatGb(disk.usedMb)} / ${formatGb(disk.totalMb)} GB)`}
      warn={disk.usagePercent >= 80}
    />
  );
}

/** Swap in use is the earliest sign of memory pressure; "none" means the OOM killer is the only fallback. */
function SwapStat({ swap }: { swap: SwapStats }) {
  return (
    <MiniStat
      label="Swap"
      value={swap.totalMb === 0 ? "none" : `${swap.usagePercent}% (${swap.usedMb} / ${swap.totalMb} MB)`}
      warn={swap.usagePercent >= 50}
    />
  );
}

function NodeCard({ node }: { node: NodeStats }) {
  const p = node.process;
  const stale = Date.now() - new Date(node.lastHeartbeatAt).getTime() > 90_000;
  const shortId = node.nodeId.split("-").slice(0, 2).join("-");

  return (
    <Card className={stale ? "opacity-60" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span className="text-sm font-mono">{shortId}</span>
          <span className={`text-xs font-normal ${stale ? "text-admin-danger-400" : "text-admin-text-subtle"}`}>
            {formatTimeAgo(node.lastHeartbeatAt)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-admin-card-border">
        <MiniStat label="Uptime" value={formatUptime(p.uptimeSeconds)} />
        <MiniStat label="Platform" value={p.system.platform} />
        <MiniStat label="RSS Memory" value={`${p.memoryMb.rss} / ${p.system.totalMemoryMb}`} unit="MB" warn={p.memoryMb.rss > p.system.totalMemoryMb * 0.5} />
        <MiniStat label="Heap Used / Total" value={`${p.memoryMb.heapUsed} / ${p.memoryMb.heapTotal}`} unit="MB" />
        <MiniStat label="External" value={p.memoryMb.external} unit="MB" />
        <MiniStat label="System Memory" value={`${p.system.memoryUsagePercent}% (${p.system.freeMemoryMb} free)`} warn={p.system.memoryUsagePercent > 90} />
        <MiniStat label="CPU User" value={p.cpuUsage.user} unit="ms" />
        <MiniStat label="CPU System" value={p.cpuUsage.system} unit="ms" />
        <MiniStat label="CPUs" value={p.system.cpuCount} />
        <MiniStat label="Load (1/5/15m)" value={p.system.loadAvg.join(" / ")} warn={p.system.loadAvg[0] > p.system.cpuCount} />
        {p.system.disk && <DiskStat disk={p.system.disk} />}
        {p.system.swap && <SwapStat swap={p.system.swap} />}
      </CardContent>
    </Card>
  );
}

/** One VM reporting through its own cron (the DB VM). A red value is past the threshold worth acting on. */
function HostCard({ host }: { host: HostStats }) {
  const s = host.stats;
  const now = Date.now();
  const stale = now - new Date(host.reportedAt).getTime() > 15 * 60_000;
  const backupAgeH = s.backup.lastCompletedAt ? (now - new Date(s.backup.lastCompletedAt).getTime()) / 3_600_000 : null;
  const archiverFailing =
    s.archiver.lastFailedAt !== null &&
    (s.archiver.lastArchivedAt === null || new Date(s.archiver.lastFailedAt) > new Date(s.archiver.lastArchivedAt));

  return (
    <Card className={stale ? "opacity-60" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span className="text-sm font-mono">{host.host}</span>
          <span className={`text-xs font-normal ${stale ? "text-admin-danger-400" : "text-admin-text-subtle"}`}>
            {formatTimeAgo(host.reportedAt)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-admin-card-border">
        {s.disks.map((d) => <DiskStat key={d.mount} disk={d} />)}
        <MiniStat label="System Memory" value={`${s.memory.usagePercent}% (${s.memory.availableMb} free)`} warn={s.memory.usagePercent > 90} />
        <SwapStat swap={s.swap} />
        <MiniStat label="Load (1/5/15m)" value={s.loadAvg.join(" / ")} />
        <MiniStat label="pg_wal" value={s.walMb} unit="MB" warn={s.walMb > 2048} />
        <MiniStat
          label="WAL archiving"
          value={archiverFailing ? `failing (${s.archiver.failedCount})` : s.archiver.lastArchivedAt ? formatTimeAgo(s.archiver.lastArchivedAt) : "never"}
          warn={archiverFailing}
        />
        <MiniStat
          label="Last backup"
          value={backupAgeH === null ? `none (${s.backup.status ?? "?"})` : `${formatTimeAgo(s.backup.lastCompletedAt!)} (${s.backup.type})`}
          warn={backupAgeH === null || backupAgeH > 26}
        />
      </CardContent>
    </Card>
  );
}

function healthBadge(row: { pending: number; running: number; failed: number }) {
  if (row.failed > 0) return <Badge variant="destructive">Feil</Badge>;
  if (row.running > 0) return <Badge variant="default">Aktiv</Badge>;
  if (row.pending > 0) return <Badge variant="secondary">Ventende</Badge>;
  return <Badge variant="outline">Inaktiv</Badge>;
}

export function InfrastructureTab() {
  const stats = useSystemStats();
  const byWorker = useJobsByWorker();
  const throughput = useThroughput();
  const health = useHealthStats();

  if (stats.isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-secondary" /></div>;
  }

  const s = stats.data as any;
  const h = health.data;

  return (
    <div className="space-y-6 mt-4">
      {s && (
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Ventende" value={s.pending} variant="pending" />
          <StatCard label="Kjorer" value={s.running} variant="running" />
          <StatCard label="Fullfort" value={s.completed} variant="completed" />
          <StatCard label="Feilet" value={s.failed} variant="failed" />
        </div>
      )}

      {/* Cluster health: per-node cards + shared PG card */}
      {h && (
        <>
          <div className={`grid gap-4 ${h.nodes.length + h.hosts.length <= 2 ? "grid-cols-2" : "grid-cols-3"}`}>
            {h.nodes.map((node) => (
              <NodeCard key={node.nodeId} node={node} />
            ))}
            {h.hosts.map((host) => (
              <HostCard key={host.host} host={host} />
            ))}
          </div>

          <Card>
            <CardHeader><CardTitle>PostgreSQL</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-x-8 divide-admin-card-border">
              <div className="divide-y divide-admin-card-border">
                <MiniStat label="Version" value={h.pg.version} />
                <MiniStat label="Uptime" value={formatUptime(h.pg.uptimeSeconds)} />
                <MiniStat label="Connections" value={`${h.pg.activeConnections} active / ${h.pg.idleConnections} idle (${h.pg.connectionUsagePercent}%)`} warn={h.pg.connectionUsagePercent > 80} />
                <MiniStat label="Max Connections" value={h.pg.maxConnections} />
                <MiniStat label="Database Size" value={h.pg.databaseSizeMb} unit="MB" />
                <MiniStat label="Cache Hit Ratio" value={`${(h.pg.cacheHitRatio * 100).toFixed(1)}%`} warn={h.pg.cacheHitRatio < 0.95} />
              </div>
              <div className="divide-y divide-admin-card-border">
                <MiniStat label="Shared Buffers" value={h.pg.sharedBuffersMb} unit="MB" />
                <MiniStat label="Work Mem" value={h.pg.workMemMb} unit="MB" />
                <MiniStat label="Deadlocks" value={h.pg.deadlocks} warn={h.pg.deadlocks > 0} />
                <MiniStat label="TX Committed" value={h.pg.transactionsCommitted.toLocaleString()} />
                <MiniStat label="TX Rolled Back" value={h.pg.transactionsRolledBack.toLocaleString()} warn={h.pg.transactionsRolledBack > 100} />
                <MiniStat label="Temp Files" value={h.pg.tempFilesMb} unit="MB" warn={h.pg.tempFilesMb > 50} />
                <MiniStat label="Slow Queries (>5s)" value={h.pg.slowQueries} warn={h.pg.slowQueries > 0} />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader><CardTitle>Jobber per worker</CardTitle></CardHeader>
        <CardContent>
          {byWorker.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Ko</TableHead>
                  <TableHead className="text-right">Totalt</TableHead>
                  <TableHead className="text-right">Ventende</TableHead>
                  <TableHead className="text-right">Kjorer</TableHead>
                  <TableHead className="text-right">Fullfort</TableHead>
                  <TableHead className="text-right">Feilet</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(byWorker.data ?? []).map((row: any) => (
                  <TableRow key={`${row.worker}-${row.queue}`}>
                    <TableCell className="font-mono text-xs">{row.worker}</TableCell>
                    <TableCell className="text-xs">{row.queue}</TableCell>
                    <TableCell className="text-right">{row.total}</TableCell>
                    <TableCell className="text-right">{row.pending}</TableCell>
                    <TableCell className="text-right">{row.running}</TableCell>
                    <TableCell className="text-right">{row.completed}</TableCell>
                    <TableCell className="text-right">{row.failed}</TableCell>
                    <TableCell>{healthBadge(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Gjennomstromning (24t)</CardTitle></CardHeader>
        <CardContent>
          {throughput.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (throughput.data ?? []).length === 0 ? (
            <p className="text-sm text-admin-text-secondary">Ingen data enna.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tidspunkt</TableHead>
                  <TableHead className="text-right">Fullfort</TableHead>
                  <TableHead className="text-right">Feilet</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(throughput.data ?? []).map((row: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">
                      {new Date(row.hour).toLocaleString("nb-NO", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                    </TableCell>
                    <TableCell className="text-right text-admin-success-400">{row.completed}</TableCell>
                    <TableCell className="text-right text-admin-danger-400">{row.failed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
